import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Play,
    Pause,
    SkipForward,
    Check,
    Clock,
    Dumbbell,
    ChevronLeft,
    ChevronRight,
    Trophy,
    Loader2,
    Plus,
    Minus,
} from 'lucide-react';

interface WorkoutExercise {
    id: string;
    exercise_id: string;
    order_index: number;
    sets: number;
    reps: string;
    weight_kg: number | null;
    rest_seconds: number;
    notes: string | null;
    exercise: {
        id: string;
        name: string;
        description: string | null;
        video_url: string | null;
        muscle_groups: string[];
    };
}

interface SetLog {
    set_number: number;
    reps_completed: number;
    weight_kg: number;
    completed: boolean;
}

export function AthleteWorkoutPage() {
    const { prescriptionId } = useParams<{ prescriptionId: string }>();
    const { user, profile, tenant, loading } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
    const [currentSetIndex, setCurrentSetIndex] = useState(0);
    const [isResting, setIsResting] = useState(false);
    const [restTime, setRestTime] = useState(0);
    const [workoutStartTime] = useState(new Date());
    const [exerciseLogs, setExerciseLogs] = useState<Record<string, SetLog[]>>({});
    const [tempWeight, setTempWeight] = useState(0);
    const [tempReps, setTempReps] = useState(0);
    const [workoutComplete, setWorkoutComplete] = useState(false);

    const restTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Buscar prescrição e exercícios
    const { data: prescription, isLoading } = useQuery({
        queryKey: ['workout-prescription', prescriptionId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_prescriptions')
                .select(`
                    *,
                    workout:workouts(
                        *,
                        blocks:workout_blocks(
                            *,
                            exercises:workout_exercises(
                                *,
                                exercise:exercises(*)
                            )
                        )
                    )
                `)
                .eq('id', prescriptionId)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!prescriptionId,
    });

    // Extrair todos os exercícios em ordem
    const allExercises: WorkoutExercise[] = prescription?.workout?.blocks
        ?.sort((a: any, b: any) => a.order_index - b.order_index)
        ?.flatMap((block: any) =>
            block.exercises
                ?.sort((a: any, b: any) => a.order_index - b.order_index)
                ?.map((ex: any) => ({
                    ...ex,
                    rest_seconds: block.rest_between_exercises_seconds || 60,
                }))
        ) || [];

    const currentExercise = allExercises[currentExerciseIndex];

    // Inicializar logs do exercício atual
    useEffect(() => {
        if (currentExercise && !exerciseLogs[currentExercise.id]) {
            const sets = Array.from({ length: currentExercise.sets || 3 }, (_, i) => ({
                set_number: i + 1,
                reps_completed: parseInt(currentExercise.reps) || 12,
                weight_kg: currentExercise.weight_kg || 0,
                completed: false,
            }));
            setExerciseLogs(prev => ({ ...prev, [currentExercise.id]: sets }));
            setTempWeight(currentExercise.weight_kg || 0);
            setTempReps(parseInt(currentExercise.reps) || 12);
        }
    }, [currentExercise]);

    // Timer de descanso
    useEffect(() => {
        if (isResting && restTime > 0) {
            restTimerRef.current = setTimeout(() => {
                setRestTime(t => t - 1);
            }, 1000);
        } else if (isResting && restTime === 0) {
            setIsResting(false);
        }

        return () => {
            if (restTimerRef.current) clearTimeout(restTimerRef.current);
        };
    }, [isResting, restTime]);

    // Mutation para salvar workout_log
    const saveLogMutation = useMutation({
        mutationFn: async () => {
            const totalDuration = Math.floor((Date.now() - workoutStartTime.getTime()) / 1000);

            // 1. Inserir o log principal
            const { data: log, error: logError } = await supabase
                .from('workout_logs')
                .insert({
                    student_id: user?.id,
                    tenant_id: tenant?.id,
                    prescription_id: prescriptionId,
                    workout_id: prescription?.workout_id,
                    started_at: workoutStartTime.toISOString(),
                    completed_at: new Date().toISOString(),
                    duration_seconds: totalDuration,
                    notes: null,
                    rating: null,
                })
                .select()
                .single();

            if (logError) throw logError;

            // 2. Preparar e inserir os sets individuais
            const allSets: any[] = [];
            Object.entries(exerciseLogs).forEach(([exerciseId, sets]) => {
                sets.forEach((set) => {
                    if (set.completed) {
                        allSets.push({
                            workout_log_id: log.id,
                            block_exercise_id: exerciseId,
                            set_number: set.set_number,
                            reps_completed: set.reps_completed,
                            weight_kg: set.weight_kg,
                        });
                    }
                });
            });

            if (allSets.length > 0) {
                const { error: setsError } = await supabase
                    .from('workout_log_sets')
                    .insert(allSets);

                if (setsError) throw setsError;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workout-logs'] });
            setWorkoutComplete(true);
        },
    });

    const markSetComplete = () => {
        if (!currentExercise) return;

        const logs = [...(exerciseLogs[currentExercise.id] || [])];
        logs[currentSetIndex] = {
            ...logs[currentSetIndex],
            reps_completed: tempReps,
            weight_kg: tempWeight,
            completed: true,
        };
        setExerciseLogs(prev => ({ ...prev, [currentExercise.id]: logs }));

        // Próximo set ou próximo exercício
        if (currentSetIndex < (currentExercise.sets || 3) - 1) {
            setCurrentSetIndex(currentSetIndex + 1);
            setIsResting(true);
            setRestTime(currentExercise.rest_seconds || 60);
        } else if (currentExerciseIndex < allExercises.length - 1) {
            setCurrentExerciseIndex(currentExerciseIndex + 1);
            setCurrentSetIndex(0);
            setIsResting(true);
            setRestTime(currentExercise.rest_seconds || 90);
        } else {
            // Treino completo
            saveLogMutation.mutate();
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
        );
    }

    if (workoutComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Trophy className="w-10 h-10 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Treino Finalizado! 💪
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Parabéns! Você completou o treino de hoje.
                    </p>
                    <div className="grid grid-cols-3 gap-4 mb-8">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                            <p className="text-2xl font-bold text-primary-600">
                                {formatTime(Math.floor((Date.now() - workoutStartTime.getTime()) / 1000))}
                            </p>
                            <p className="text-xs text-gray-500">Duração</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                            <p className="text-2xl font-bold text-primary-600">
                                {Object.keys(exerciseLogs).length}
                            </p>
                            <p className="text-xs text-gray-500">Exercícios</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                            <p className="text-2xl font-bold text-primary-600">
                                {Object.values(exerciseLogs).flat().filter(s => s.completed).length}
                            </p>
                            <p className="text-xs text-gray-500">Séries</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/atleta')}
                        className="btn-primary w-full"
                    >
                        Voltar ao Início
                    </button>
                </div>
            </div>
        );
    }

    if (!currentExercise) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p className="text-gray-600 dark:text-gray-400">Nenhum exercício encontrado</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3 flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="p-2">
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="text-center">
                    <p className="text-sm text-gray-500">
                        Exercício {currentExerciseIndex + 1} de {allExercises.length}
                    </p>
                    <div className="flex items-center gap-1 justify-center">
                        {allExercises.map((_, i) => (
                            <div
                                key={i}
                                className={`w-2 h-2 rounded-full ${i < currentExerciseIndex
                                    ? 'bg-green-500'
                                    : i === currentExerciseIndex
                                        ? 'bg-primary-500'
                                        : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">
                        {formatTime(Math.floor((Date.now() - workoutStartTime.getTime()) / 1000))}
                    </span>
                </div>
            </div>

            {/* Rest Timer Overlay */}
            {isResting && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
                    <div className="text-center text-white">
                        <p className="text-lg mb-2">Descanse</p>
                        <p className="text-8xl font-bold mb-4">{formatTime(restTime)}</p>
                        <button
                            onClick={() => setIsResting(false)}
                            className="btn-secondary"
                        >
                            <SkipForward className="w-5 h-5 mr-2" />
                            Pular
                        </button>
                    </div>
                </div>
            )}

            {/* Exercise Content */}
            <div className="p-4 space-y-6">
                {/* Exercise Info */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                            <Dumbbell className="w-8 h-8 text-primary-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {currentExercise.exercise?.name}
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                {currentExercise.sets} séries × {currentExercise.reps} reps
                            </p>
                            {currentExercise.notes && (
                                <p className="text-primary-600 text-sm mt-1">{currentExercise.notes}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Current Set */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                    <div className="text-center mb-6">
                        <p className="text-gray-500 text-sm mb-1">Série Atual</p>
                        <p className="text-4xl font-bold text-gray-900 dark:text-white">
                            {currentSetIndex + 1} / {currentExercise.sets || 3}
                        </p>
                    </div>

                    {/* Weight Adjuster */}
                    <div className="mb-6">
                        <label className="block text-sm text-gray-600 dark:text-gray-400 text-center mb-2">
                            Peso (kg)
                        </label>
                        <div className="flex items-center justify-center gap-4">
                            <button
                                onClick={() => setTempWeight(w => Math.max(0, w - 2.5))}
                                className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center"
                            >
                                <Minus className="w-5 h-5" />
                            </button>
                            <span className="text-4xl font-bold text-gray-900 dark:text-white w-24 text-center">
                                {tempWeight}
                            </span>
                            <button
                                onClick={() => setTempWeight(w => w + 2.5)}
                                className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Reps Adjuster */}
                    <div className="mb-6">
                        <label className="block text-sm text-gray-600 dark:text-gray-400 text-center mb-2">
                            Repetições
                        </label>
                        <div className="flex items-center justify-center gap-4">
                            <button
                                onClick={() => setTempReps(r => Math.max(1, r - 1))}
                                className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center"
                            >
                                <Minus className="w-5 h-5" />
                            </button>
                            <span className="text-4xl font-bold text-gray-900 dark:text-white w-24 text-center">
                                {tempReps}
                            </span>
                            <button
                                onClick={() => setTempReps(r => r + 1)}
                                className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Complete Button */}
                    <button
                        onClick={markSetComplete}
                        disabled={saveLogMutation.isPending}
                        className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2"
                    >
                        {saveLogMutation.isPending ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Check className="w-5 h-5" />
                        )}
                        Série Completa
                    </button>
                </div>

                {/* Sets Progress */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Séries</p>
                    <div className="flex gap-2">
                        {(exerciseLogs[currentExercise.id] || []).map((set, i) => (
                            <div
                                key={i}
                                className={`flex-1 py-3 rounded-lg text-center text-sm font-medium ${set.completed
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                    : i === currentSetIndex
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 ring-2 ring-primary-500'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                                    }`}
                            >
                                {set.completed ? (
                                    <Check className="w-4 h-4 mx-auto" />
                                ) : (
                                    i + 1
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
